import Layout from '../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function AboutPage() {
  // SEO設定
  useSEO({
    title: 'このサイトについて｜キョウコレ',
    description: 'キョウコレは、学校や居場所、家庭での「活用レシピ」を共有するためのサイトです。教材サイトや単元ガイドと組み合わせて、日々の実践に役立つ情報を見つけることができます。',
    keywords: 'キョウコレについて,サイト概要,活用レシピ,Boratasu,学習支援プラットフォーム',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/about',
    jsonLd: generateWebPageSchema(
      'このサイトについて｜キョウコレ',
      'キョウコレは、学校や居場所、家庭での「活用レシピ」を共有するためのサイトです。教材サイトや単元ガイドと組み合わせて、日々の実践に役立つ情報を見つけることができます。',
      '/about'
    ),
  });

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ページタイトル */}
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          このサイトについて（キョウコレ）
        </h1>

        {/* セクション1：キョウコレとは */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            キョウコレとは
          </h2>
          <div className="text-gray-700 leading-relaxed space-y-3">
            <p>
              キョウコレは、学校や居場所、家庭での「活用レシピ」を共有するためのサイトです。
            </p>
            <p>
              授業・居場所・行事などの場面で、他の先生や支援者の工夫を参考にできます。
              教材サイトや単元ガイドと組み合わせて、日々の実践に役立つ情報を見つけることができます。
            </p>
          </div>
        </section>

        {/* セクション2：できること */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            できること
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>
                <strong>活用レシピを検索・閲覧する</strong>
                <br />
                教科・シーン・学年・タグ・「役に立った」などでフィルタして、目的に合ったレシピを探せます。
              </span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>
                <strong>自分の活用レシピを投稿する</strong>
                <br />
                管理者の承認後に公開されます。他の先生や支援者と実践のアイデアを共有できます。
              </span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>
                <strong>マイページで振り返る</strong>
                <br />
                自分の投稿レシピや「役に立った」を押したレシピを一覧で確認できます。
              </span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>
                <strong>単元ガイドや教材サイトを参照する</strong>
                <br />
                活用レシピと関連する単元ガイドや教材サイトを確認できます。
              </span>
            </li>
          </ul>
        </section>

        {/* セクション3：キョウコレと Boratasu の関係 */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            キョウコレと Boratasu の関係
          </h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              キョウコレは、「授業・居場所・家庭での学び方のアイデア（活用レシピ）」を共有するサイトです。
              一方で、オンラインで学習支援を届けるためのインフラとして「Boratasu（ボラタス）」というサービスも運営しています。
            </p>
            
            <ul className="space-y-2 my-4">
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>
                  <strong>キョウコレ：</strong>アイデアやノウハウを集めて整理する場所
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>
                  <strong>Boratasu：</strong>そのアイデアを、オンライン学習支援として実際に試すための場
                </span>
              </li>
            </ul>

            <p>
              たとえば、キョウコレで見つけた「算数のゲーム的な学び方」を、
              Boratasu を使ってオンラインのボランティアと一緒に実践する、といった使い方ができます。
            </p>

            <p>
              どちらか一方だけでも利用できますが、組み合わせて使うことで、
              「アイデア」と「実践」がゆるやかにつながることを目指しています。
            </p>

            <div className="mt-6">
              <a
                href="https://zion-craft.work/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg bg-teal-600 px-6 py-3 text-base font-medium text-white hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                ボラタスについて詳しく知る
                <i className="ri-external-link-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        {/* セクション4：利用上の注意 */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            利用上の注意
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3 text-gray-700">
            <p className="flex items-start">
              <i className="ri-alert-line text-amber-600 mr-2 mt-0.5 flex-shrink-0"></i>
              <span>
                <strong>個人情報を含めないでください</strong>
                <br />
                児童生徒の氏名、顔写真、学校名など、個人を特定できる情報は投稿しないようお願いします。
              </span>
            </p>
            <p className="flex items-start">
              <i className="ri-alert-line text-amber-600 mr-2 mt-0.5 flex-shrink-0"></i>
              <span>
                <strong>著作権に配慮してください</strong>
                <br />
                教材の画像や資料など、著作権で保護されているコンテンツをそのまま掲載することは避けてください。
              </span>
            </p>
            <p className="flex items-start">
              <i className="ri-alert-line text-amber-600 mr-2 mt-0.5 flex-shrink-0"></i>
              <span>
                <strong>投稿内容は管理者が確認します</strong>
                <br />
                投稿されたレシピは管理者が確認し、必要に応じて修正依頼や非公開にすることがあります。
              </span>
            </p>
          </div>
        </section>

        {/* β版について */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            β版について
          </h2>
          <div className="text-gray-700 leading-relaxed space-y-3">
            <p>
              現在、キョウコレはβ版として公開しています。
              機能の追加や改善を継続的に行っていますので、使いにくい点やバグがあればお気軽にフィードバックをお寄せください。
            </p>
            <p>
              <a href="/feedback" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                フィードバックページはこちら
              </a>
            </p>
          </div>
        </section>

        {/* CTAボタン */}
        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            href="/recipes"
            className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-6 py-3 text-base font-medium text-white hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line mr-2"></i>
            活用レシピを探す
          </a>
          <a
            href="/feedback"
            className="inline-flex items-center justify-center rounded-lg border border-teal-600 px-6 py-3 text-base font-medium text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-feedback-line mr-2"></i>
            フィードバックを送る
          </a>
        </div>
      </div>
    </Layout>
  );
}
